import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Reports() {
  const { salesReports, loading } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!salesReports) return;

    let result = [...salesReports];

    // Search Logic
    if (searchTerm) {
        const keywords = searchTerm.toLowerCase().split(" ").filter(w => w);
        result = result.filter(r => {
            const dateStr = r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            
            const row = `
                ${dateStr} 
                ${r.userName} 
                ${r.hospital} 
                ${r.city} ${r.state} 
                ${r.person} 
                ${r.outcome} 
                ${r.discussion} 
                ${r.product} 
                ${r.department}
            `.toLowerCase();
            
            return keywords.every(w => row.includes(w));
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setFiltered(result);
  }, [salesReports, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(r => ({
        Date: formatDate(r.createdAt),
        Time: formatTimeOnly(r.createdAt),
        Employee: r.userName,
        Hospital: r.hospital,
        City: r.city || "-",
        State: r.state || "-",
        Met_With: r.person,
        Designation: r.designation || "-",
        Contact_No: r.contactNo || "-",
        Email: r.email || "-",
        Department: r.department || "-",
        Product_Discussed: r.product || "-",
        Discussion_Notes: r.discussion,
        Outcome: r.outcome,
        Next_Follow_Up: r.nextFollowUp || "-",
        Location_Link: r.latitude ? `https://maps.google.com/?q=${r.latitude},${r.longitude}` : "-"
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales_Visits");
    XLSX.writeFile(wb, `Sales_Reports_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "sales_reports", editData.id), {
              hospital: editData.hospital,
              city: editData.city,
              person: editData.person,
              outcome: editData.outcome,
              discussion: editData.discussion,
              nextFollowUp: editData.nextFollowUp
          });
          alert("Report Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this report?")) await deleteDoc(doc(db, "sales_reports", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  const formatTimeOnly = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit', hour12:true});
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#2c3e50", display:"flex", alignItems:"center"}}>
                📊 Sales Visits <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search (Name, Date, City...)" value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#2c3e50", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Hospital & City</th>
                    <th style={thStyle}>Met With</th>
                    <th style={thStyle}>Outcome</th>
                    <th style={thStyle}>Next FollowUp</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No visits found.</td></tr>
              ) : filtered.map(r => {
                const today = new Date().toISOString().split('T')[0];
                const isOverdue = r.nextFollowUp && r.nextFollowUp <= today;
                return (
                    <tr key={r.id} style={{borderBottom:"1px solid #eee", height:"45px", backgroundColor: isOverdue ? "#fff3cd" : "white"}}>
                        <td style={tdStyle}>
                            {formatDate(r.createdAt)}
                            <br/><span style={{fontSize:"9px", color:"gray"}}>{formatTimeOnly(r.createdAt)}</span>
                        </td>
                        <td style={{...tdStyle, fontWeight:"bold"}}>{r.userName}</td>
                        <td style={{...tdStyle, textAlign:"left"}}>
                            <div style={{fontWeight:"bold"}}>{r.hospital}</div>
                            <div style={{fontSize:"10px", color:"gray"}}>{r.city || '-'}</div>
                        </td>
                        <td style={tdStyle}>
                            {r.person}
                            <br/><span style={{fontSize:"9px", color:"blue"}}>{r.contactNo}</span>
                        </td>
                        <td style={{...tdStyle, color:"#e65100", fontWeight:"bold"}}>{r.outcome}</td>
                        <td style={{...tdStyle, color: isOverdue?"red":"black", fontWeight: isOverdue?"bold":"normal"}}>
                            {r.nextFollowUp ? formatDate(r.nextFollowUp) : "-"}
                        </td>
                        <td style={tdStyle}>
                            <div style={{display:"flex", gap:"3px"}}>
                                <button onClick={() => setViewData(r)} style={btnIconEye} title="View">👁️</button>
                                <button onClick={() => setEditData(r)} style={btnIconEdit} title="Edit">✏️</button>
                                <button onClick={() => handleDelete(r.id)} style={btnIconDelete} title="Delete">🗑️</button>
                            </div>
                        </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#2c3e50"}}>📊 Visit Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px"}}>
                    <div>
                        <p><strong>📅 Date:</strong> {formatDate(viewData.createdAt)}</p>
                        <p><strong>⏰ Time:</strong> {formatTimeOnly(viewData.createdAt)}</p>
                        <p><strong>🏥 Hospital:</strong> {viewData.hospital}</p>
                        <p><strong>📍 City:</strong> {viewData.city}, {viewData.state}</p>
                    </div>
                    <div>
                        <p><strong>👤 Employee:</strong> {viewData.userName}</p>
                        <p><strong>🤝 Met:</strong> {viewData.person}</p>
                        <p><strong>📞 Contact:</strong> {viewData.contactNo || '-'}</p>
                        <p><strong>📧 Email:</strong> {viewData.email || '-'}</p>
                    </div>
                </div>
                
                <hr style={{border:"0.5px solid #eee"}}/>
                <p><strong>🏢 Dept:</strong> {viewData.department || '-'} | <strong>📦 Product:</strong> {viewData.product || '-'}</p>
                
                <div style={{background:"#f9f9f9", padding:"10px", marginTop:"10px", borderRadius:"5px", borderLeft:"4px solid #3498db"}}>
                    <p style={{margin:0, fontWeight:"bold", color:"#3498db"}}>📝 Discussion / Notes:</p>
                    <p style={{marginTop:"5px"}}>{viewData.discussion}</p>
                </div>

                <div style={{marginTop:"10px", display:"flex", justifyContent:"space-between"}}>
                    <span>🏁 Outcome: <strong>{viewData.outcome}</strong></span>
                    <span>🔜 Next Follow-up: <strong>{viewData.nextFollowUp ? formatDate(viewData.nextFollowUp) : 'None'}</strong></span>
                </div>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Report</h3>
            
            <label style={labelStyle}>Hospital Name</label>
            <input type="text" value={editData.hospital} onChange={(e)=>setEditData({...editData, hospital:e.target.value})} style={modalInput}/>
            
            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>City</label>
                    <input type="text" value={editData.city} onChange={(e)=>setEditData({...editData, city:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Met With</label>
                    <input type="text" value={editData.person} onChange={(e)=>setEditData({...editData, person:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Discussion Points</label>
            <textarea value={editData.discussion} onChange={(e)=>setEditData({...editData, discussion:e.target.value})} style={{...modalInput, height:"60px"}}></textarea>

            <label style={labelStyle}>Outcome</label>
            <select value={editData.outcome} onChange={(e)=>setEditData({...editData, outcome:e.target.value})} style={modalInput}>
                <option>Cold</option><option>Warm</option><option>Hot</option><option>Closed</option><option>Demo</option>
            </select>

            <label style={labelStyle}>Next Follow-up Date</label>
            <input type="date" value={editData.nextFollowUp} onChange={(e)=>setEditData({...editData, nextFollowUp:e.target.value})} style={modalInput}/>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"420px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#2c3e50", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default Reports;